import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@daater/shared';
import { Button } from '@daater/ui';
import { useLoginMutation, useUpdateTermsMutation } from '../api/queries';
import { postAnalyticsEvent, fetchCurrentAccount } from '../api/endpoints';
import {
  buildPaymentRedirectSearch,
  resolveCurrentAccountAccess,
} from '../api/current-account-access';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';
import { useAuthStore, type AuthUser } from '../lib/auth-store';
import { useAccountStore } from '../lib/account-store';

export default function LoginScreen() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(state => state.setAuth);
  const token = useAuthStore(state => state.token);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const setAccount = useAccountStore(state => state.setAccount);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const loginMutation = useLoginMutation();
  const updateTermsMutation = useUpdateTermsMutation();
  const [pendingTerms, setPendingTerms] = React.useState<{ user: AuthUser } | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const resolvePostLoginAccess = React.useCallback(
    async (user: AuthUser, authToken: string) => {
      setAuth(user, authToken);

      const access = await resolveCurrentAccountAccess(fetchCurrentAccount);
      if (access.account) {
        setAccount(access.account);
      }

      if (access.decision === 'payment') {
        void navigate({
          to: '/payment',
          replace: true,
          search: buildPaymentRedirectSearch('', access.amount),
        });
        return;
      }

      if (access.decision === 'allow') {
        void navigate({ to: '/', replace: true });
        return;
      }

      clearAuth();
      clearAccount();
      setErrorMessage('No pudimos validar el estado de tu cuenta. Inténtalo nuevamente.');
    },
    [clearAccount, clearAuth, navigate, setAccount, setAuth]
  );

  const onSubmit = async (values: LoginInput) => {
    setErrorMessage(null);
    try {
      const response = await loginMutation.mutateAsync(values);

      if (response.user.terms) {
        await resolvePostLoginAccess(response.user, response.token);
      } else {
        setAuth(response.user, response.token);
        setPendingTerms({ user: response.user });
      }
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const acceptTerms = async () => {
    await updateTermsMutation.mutateAsync();
    void postAnalyticsEvent({
      user: pendingTerms?.user._id,
      event: 'Terminos y Condiciones',
      module: 'Login',
      value: {
        message: 'El usuario aceptó los terminos y condiciones',
        date: new Date().toISOString(),
      },
    }).catch(() => null);
    if (pendingTerms?.user && token) {
      await resolvePostLoginAccess({ ...pendingTerms.user, terms: true }, token);
    }
    setPendingTerms(null);
  };

  const declineTerms = () => {
    clearAuth();
    clearAccount();
    setPendingTerms(null);
  };

  return (
    <div className="flex min-h-screen">
      {/* Left decorative panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 lg:flex-col lg:items-center lg:justify-center bg-[#1a2e58] px-12 text-white">
        <div className="mx-auto max-w-md space-y-8">
          <DaaterLogoHomeLink
            alt="Daater"
            className="inline-flex w-fit"
            imgClassName="h-12 brightness-0 invert"
          />
          <blockquote className="border-l-2 border-white/30 pl-5 text-lg leading-relaxed text-white/80">
            Inteligencia de datos de comercio exterior al alcance de tu equipo.
          </blockquote>
          <p className="text-sm text-white/50">
            Analiza importaciones, exportaciones y tendencias con filtros avanzados y reportes en
            tiempo real.
          </p>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex w-full items-center justify-center bg-pale-grey px-4 lg:w-1/2">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          {/* Logo visible only on mobile */}
          <div className="mb-6 flex justify-center lg:hidden">
            <DaaterLogoHomeLink alt="Daater" imgClassName="h-10" />
          </div>

          <h1 className="text-2xl font-semibold text-charcoal-grey">Iniciar sesión</h1>
          <p className="mt-1 text-sm text-slate">Ingresa tu correo y contraseña para continuar.</p>

          <form className="mt-6 space-y-4" onSubmit={event => void handleSubmit(onSubmit)(event)}>
            <div>
              <label className="text-xs font-semibold uppercase text-slate">Correo</label>
              <input
                type="email"
                {...register('email')}
                className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                placeholder="correo@empresa.com"
              />
              {errors.email ? (
                <p className="mt-1 text-xs text-darkish-pink">{errors.email.message}</p>
              ) : null}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase text-slate">Contraseña</label>
              <input
                type="password"
                {...register('password')}
                className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                placeholder="••••••••"
              />
              {errors.password ? (
                <p className="mt-1 text-xs text-darkish-pink">{errors.password.message}</p>
              ) : null}
            </div>

            {errorMessage ? (
              <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                {errorMessage}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between text-xs text-slate">
            <button
              type="button"
              onClick={() => void navigate({ to: '/password-recovery' })}
              className="underline"
            >
              Olvidé mi contraseña
            </button>
            <button
              type="button"
              onClick={() => void navigate({ to: '/signup/demo' })}
              className="underline"
            >
              Registrarme
            </button>
          </div>
        </div>
      </div>

      {/* Terms & Conditions modal */}
      {pendingTerms ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-charcoal-grey">Términos y condiciones</h2>
            <p className="mt-2 text-sm text-slate">
              Para continuar debes aceptar los términos y condiciones.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={declineTerms}>
                Cancelar
              </Button>
              <Button onClick={() => void acceptTerms()} disabled={updateTermsMutation.isPending}>
                Aceptar
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
